import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { BadgeIcon } from "@/pages/AcademyPage";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { ChevronLeft, CheckCircle2, Circle, BookOpen, ListChecks, GitBranch, ScanSearch, History, MessagesSquare, Send, ArrowRight, Award } from "lucide-react";

const TYPE_ICON = { reading: BookOpen, quiz: ListChecks, scenario: GitBranch, hotspot: ScanSearch };

/* ---------- Reading ---------- */
function Reading({ lesson, onComplete, done }) {
  return (
    <div>
      {lesson.image && <img src={lesson.image} alt="" className="w-full h-52 object-cover rounded-md mb-5" />}
      <p className="text-white/80 leading-relaxed whitespace-pre-line">{lesson.body}</p>
      <Button onClick={onComplete} className="mt-6 bg-gold text-charcoal hover:bg-gold-hover" data-testid="reading-complete-btn">
        {done ? "Marked read ✓ — continue" : "Mark as read"} <ArrowRight className="w-4 h-4 ml-1" />
      </Button>
    </div>
  );
}

/* ---------- Quiz ---------- */
function Quiz({ lesson, moduleId, onGraded, done }) {
  const qs = lesson.questions || [];
  const [sel, setSel] = useState({});
  const [result, setResult] = useState(null);
  const submit = async () => {
    const answers = qs.map((_, i) => (sel[i] ?? -1));
    try {
      const { data } = await api.post(`/academy/modules/${moduleId}/lessons/${lesson.lesson_id}/complete`, { answers });
      setResult(data.graded);
      onGraded(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-5">
      {qs.map((q, i) => (
        <div key={i} className="card-surface rounded-md p-4" data-testid={`quiz-q-${i}`}>
          <p className="text-white font-medium mb-3">{i + 1}. {q.q}</p>
          <div className="space-y-2">
            {q.options.map((opt, j) => {
              const chosen = sel[i] === j;
              const showRes = result && result.per_question[i];
              const isAnswer = showRes && j === q.answer;
              const isWrongPick = showRes && chosen && j !== q.answer;
              return (
                <button key={j} disabled={!!result} onClick={() => setSel((s) => ({ ...s, [i]: j }))}
                  data-testid={`quiz-q${i}-opt${j}`}
                  className={`w-full text-left px-3 py-2 rounded border text-sm transition-colors ${isAnswer ? "border-gold bg-gold/10 text-white" : isWrongPick ? "border-red-400/60 bg-red-400/10 text-white" : chosen ? "border-gold/60 text-white" : "border-white/15 text-white/70 hover:border-white/30"}`}>
                  {opt}
                </button>
              );
            })}
          </div>
          {result && <p className="mt-2 text-xs text-white/50">{result.per_question[i].correct ? "✓ Correct. " : "✗ Not quite. "}{q.explanation}</p>}
        </div>
      ))}
      {!result ? (
        <Button onClick={submit} disabled={Object.keys(sel).length < qs.length} className="bg-gold text-charcoal hover:bg-gold-hover" data-testid="quiz-submit-btn">Submit answers</Button>
      ) : (
        <p className="label-mono text-gold" data-testid="quiz-score">Score: {result.correct}/{result.total} — {result.passed ? "Passed" : "Review and keep going"}</p>
      )}
    </div>
  );
}

/* ---------- Branching Scenario ---------- */
function Scenario({ lesson, moduleId, onGraded, done }) {
  const sc = lesson.scenario || { nodes: [] };
  const nodeMap = Object.fromEntries((sc.nodes || []).map((n) => [n.node_id, n]));
  const caseNodes = (sc.nodes || []).filter((n) => !n.is_end);
  const [current, setCurrent] = useState(sc.start);
  const [stats, setStats] = useState({ decisions: 0, correct: 0 });
  const [feedback, setFeedback] = useState(null);
  const [pending, setPending] = useState(null);
  const [finished, setFinished] = useState(false);
  const node = nodeMap[current];
  if (!node) return <p className="text-white/50">Scenario unavailable.</p>;
  const caseIdx = caseNodes.findIndex((n) => n.node_id === current) + 1;

  const choose = (c) => {
    setStats((s) => ({ decisions: s.decisions + 1, correct: s.correct + (c.correct ? 1 : 0) }));
    setFeedback({ text: c.feedback, correct: c.correct });
    setPending(c.goto);
  };

  const advance = async () => {
    const nextId = pending;
    setPending(null); setFeedback(null);
    const next = nodeMap[nextId];
    if (next && next.is_end) {
      setFinished(true);
      const score = stats.decisions ? stats.correct / stats.decisions : 1;
      try {
        const { data } = await api.post(`/academy/modules/${moduleId}/lessons/${lesson.lesson_id}/complete`, { score });
        onGraded(data);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    }
    setCurrent(nextId);
  };

  const atEnd = pending && nodeMap[pending]?.is_end;

  return (
    <div>
      {!node.is_end && caseNodes.length > 1 && (
        <div className="label-mono text-gold mb-2">Case {caseIdx} of {caseNodes.length}</div>
      )}
      {node.image && <img src={node.image} alt="" className="w-full h-48 object-cover rounded-md mb-4" />}
      <p className="text-white/85 leading-relaxed">{node.text}</p>

      {!feedback && !node.is_end && (
        <div className="mt-4 space-y-2">
          {(node.choices || []).map((c, i) => (
            <button key={i} onClick={() => choose(c)}
              data-testid={`scenario-choice-${i}`}
              className="w-full text-left px-4 py-3 rounded border border-white/15 text-sm text-white/80 hover:border-gold/50 hover:text-white transition-colors">
              {c.label}
            </button>
          ))}
        </div>
      )}

      {feedback && (
        <>
          <div className={`mt-4 rounded p-3 text-sm ${feedback.correct ? "bg-gold/10 border border-gold/40 text-white" : "bg-red-400/10 border border-red-400/40 text-white"}`} data-testid="scenario-feedback">
            <span className="font-semibold">{feedback.correct ? "\u2713 Strong call. " : "\u2717 Here's the cost. "}</span>{feedback.text}
          </div>
          <div className="mt-3 text-right">
            <Button onClick={advance} className="bg-gold text-charcoal hover:bg-gold-hover" data-testid="scenario-continue-btn">
              {atEnd ? "See result" : "Continue to next case"} <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </>
      )}

      {node.is_end && finished && <p className="mt-4 label-mono text-gold" data-testid="scenario-done">Scenario complete — {stats.correct}/{stats.decisions} strong calls.</p>}
    </div>
  );
}

/* ---------- Photo Hotspot ---------- */
function Hotspot({ lesson, moduleId, onGraded }) {
  const hs = lesson.hotspot || { clues: [] };
  const [found, setFound] = useState([]);
  const [msg, setMsg] = useState("");
  const all = hs.clues.length;
  const done = found.length >= all && all > 0;

  const tap = async (i) => {
    if (found.includes(i)) return;
    const nf = [...found, i];
    setFound(nf);
    setMsg(hs.clues[i].found_text);
    if (nf.length >= all) {
      try {
        const { data } = await api.post(`/academy/modules/${moduleId}/lessons/${lesson.lesson_id}/complete`, { score: 1 });
        onGraded(data);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    }
  };

  return (
    <div>
      <p className="text-white/70 mb-3">{hs.instructions}</p>
      <div className="relative w-full rounded-md overflow-hidden border border-white/10" data-testid="hotspot-image">
        <img src={hs.image} alt="" className="w-full h-auto block" />
        {hs.clues.map((c, i) => (
          <button key={i} onClick={() => tap(i)} data-testid={`hotspot-${i}`}
            className={`absolute -translate-x-1/2 -translate-y-1/2 rounded-full border-2 flex items-center justify-center text-xs font-bold transition-all ${found.includes(i) ? "bg-gold border-gold text-charcoal" : "bg-black/40 border-white/70 text-white animate-pulse hover:scale-110"}`}
            style={{ left: `${c.x}%`, top: `${c.y}%`, width: 34, height: 34 }}>
            {found.includes(i) ? "✓" : "?"}
          </button>
        ))}
      </div>
      <div className="mt-2 flex items-center justify-between label-mono">
        <span className="text-white/50">Found {found.length}/{all}</span>
        {done && <span className="text-gold" data-testid="hotspot-done">All evidence identified ✓</span>}
      </div>
      {msg && <p className="mt-2 text-sm text-white/70" data-testid="hotspot-feedback">{msg}</p>}
    </div>
  );
}

/* ---------- Module discussion (reuses community threads keyed by module_id) ---------- */
function ModuleDiscussion({ moduleId, prompt }) {
  const [threads, setThreads] = useState([]);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const load = useCallback(() => api.get(`/cases/${moduleId}/threads`).then(({ data }) => setThreads(data)).catch(() => {}), [moduleId]);
  useEffect(() => { load(); }, [load]);
  const post = async () => {
    if (!title.trim() || !body.trim()) return;
    try { await api.post(`/cases/${moduleId}/threads`, { board: "Academy", title, body }); setTitle(""); setBody(""); load(); toast.success("Posted to the module discussion"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="card-surface rounded-md p-5 mt-8" data-testid="module-discussion">
      <div className="flex items-center gap-2 mb-1"><MessagesSquare className="w-4 h-4 text-gold" /><h3 className="font-display font-semibold text-white">Case analysis discussion</h3></div>
      {prompt && <p className="text-sm text-white/60 mb-4">{prompt}</p>}
      <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title your analysis…" className="bg-charcoal border-white/15 text-white mb-2" data-testid="discussion-title-input" />
      <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} placeholder="Share your reasoning — the Community Assistant reads every thread." className="bg-charcoal border-white/15 text-white mb-2" data-testid="discussion-body-input" />
      <Button onClick={post} className="bg-gold text-charcoal hover:bg-gold-hover" data-testid="discussion-post-btn"><Send className="w-4 h-4 mr-1" /> Post analysis</Button>
      <div className="mt-5 space-y-2">
        {threads.map((t) => (
          <Link key={t.thread_id} to={`/threads/${t.thread_id}`} className="block rounded border border-white/10 px-3 py-2 hover:border-gold/40 transition-colors" data-testid={`discussion-thread-${t.thread_id}`}>
            <span className="text-sm text-white/85">{t.title}</span>
            <span className="label-mono text-white/40 ml-2">{t.comment_count || 0} replies</span>
          </Link>
        ))}
        {threads.length === 0 && <p className="text-sm text-white/40">Be the first to post your analysis.</p>}
      </div>
    </div>
  );
}

export default function AcademyModulePage() {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const [mod, setMod] = useState(null);
  const [state, setState] = useState({});   // lesson_id -> {done}
  const [active, setActive] = useState(0);

  const load = useCallback(() => {
    api.get(`/academy/modules/${moduleId}`).then(({ data }) => {
      setMod(data);
      setState(data.progress?.lesson_state || {});
    }).catch((e) => { toast.error(formatApiError(e.response?.data?.detail)); navigate("/academy"); });
  }, [moduleId, navigate]);
  useEffect(() => { load(); }, [load]);

  const afterComplete = (lessonId, resp) => {
    setState((s) => ({ ...s, [lessonId]: { done: true } }));
    if (resp?.module_completed) {
      toast.success(`Module complete — badge earned: ${resp.earned_badge?.label || ""}`);
      if (resp.certified) toast.success("🏅 You're a Certified Detective!");
    }
  };

  if (!mod) return <div className="min-h-screen bg-charcoal"><Navbar /><div className="label-mono animate-pulse py-20 text-center">Loading module…</div></div>;
  const lessons = mod.lessons || [];
  const lesson = lessons[active];
  const tvn = mod.then_vs_now || {};
  const allDone = lessons.length > 0 && lessons.every((l) => state[l.lesson_id]?.done);
  const isLast = active >= lessons.length - 1;

  return (
    <div className="min-h-screen bg-charcoal">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        <button onClick={() => navigate("/academy")} className="label-mono hover:text-gold transition-colors inline-flex items-center gap-1 mb-6" data-testid="academy-back-btn"><ChevronLeft className="w-3 h-3" /> The Academy</button>
        <div className="label-mono text-gold">Week {mod.week}{mod.is_staff_preview ? " · staff preview" : ""}</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-white mt-1">{mod.title}</h1>
        <p className="text-white/60 mt-2">{mod.summary}</p>
        {mod.badge?.id && (
          <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-gold/30 bg-gold/5 px-3 py-1.5">
            <BadgeIcon name={mod.badge.icon} className="w-4 h-4 text-gold" /><span className="text-xs text-white/80">Complete to earn: {mod.badge.label}</span>
          </div>
        )}

        {(tvn.then || tvn.now) && (
          <div className="mt-6 rounded-md border border-white/10 bg-white/[0.03] p-4" data-testid="then-vs-now">
            <div className="flex items-center gap-2 mb-2"><History className="w-4 h-4 text-gold" /><span className="label-mono text-gold">Then vs Now</span></div>
            {tvn.then && <p className="text-sm text-white/70"><span className="text-white/40">Then:</span> {tvn.then}</p>}
            {tvn.now && <p className="text-sm text-white/70 mt-1"><span className="text-white/40">Now:</span> {tvn.now}</p>}
            {tvn.same && <p className="text-sm text-white/70 mt-1"><span className="text-gold/80">What stays the same:</span> {tvn.same}</p>}
          </div>
        )}

        {/* Lesson stepper */}
        <div className="flex flex-wrap gap-2 mt-8">
          {lessons.map((l, i) => {
            const I = TYPE_ICON[l.type] || Circle;
            const dn = state[l.lesson_id]?.done;
            return (
              <button key={l.lesson_id} onClick={() => setActive(i)} data-testid={`lesson-tab-${i}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs transition-colors ${i === active ? "border-gold bg-gold/10 text-white" : "border-white/15 text-white/60 hover:border-white/30"}`}>
                {dn ? <CheckCircle2 className="w-3.5 h-3.5 text-gold" /> : <I className="w-3.5 h-3.5" />} {l.title}
              </button>
            );
          })}
        </div>

        {/* Active lesson */}
        <div className="card-surface rounded-md p-6 mt-4" data-testid="lesson-panel">
          <h2 className="font-display text-xl font-bold text-white mb-4">{lesson.title}</h2>
          {lesson.type === "reading" && <Reading lesson={lesson} done={state[lesson.lesson_id]?.done} onComplete={async () => {
            try { await api.post(`/academy/modules/${moduleId}/lessons/${lesson.lesson_id}/complete`, {}).then(({ data }) => afterComplete(lesson.lesson_id, data)); if (active < lessons.length - 1) setActive(active + 1); }
            catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
          }} />}
          {lesson.type === "quiz" && <Quiz lesson={lesson} moduleId={moduleId} done={state[lesson.lesson_id]?.done} onGraded={(d) => afterComplete(lesson.lesson_id, d)} />}
          {lesson.type === "scenario" && <Scenario lesson={lesson} moduleId={moduleId} done={state[lesson.lesson_id]?.done} onGraded={(d) => afterComplete(lesson.lesson_id, d)} />}
          {lesson.type === "hotspot" && <Hotspot lesson={lesson} moduleId={moduleId} onGraded={(d) => afterComplete(lesson.lesson_id, d)} />}
        </div>

        {/* Navigation between lessons + back to the Academy */}
        <div className="mt-4 flex items-center justify-between gap-3">
          <Button variant="outline" onClick={() => navigate("/academy")} className="border-white/15 text-white/80 bg-transparent" data-testid="module-back-btn">
            <ChevronLeft className="w-4 h-4 mr-1" /> The Academy
          </Button>
          {!isLast ? (
            <Button onClick={() => setActive(active + 1)} className="bg-gold text-charcoal hover:bg-gold-hover" data-testid="next-lesson-btn">
              Next lesson <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={() => navigate("/academy")} className="bg-gold text-charcoal hover:bg-gold-hover" data-testid="finish-module-btn">
              Back to the Academy <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>

        {allDone && (
          <div className="mt-6 rounded-md border border-gold/50 bg-gradient-to-br from-gold/15 to-transparent p-5 text-center" data-testid="module-complete-banner">
            <BadgeIcon name={mod.badge?.icon} className="w-8 h-8 text-gold mx-auto" />
            <h3 className="font-display text-xl font-bold text-white mt-2">Module complete</h3>
            <p className="text-white/70 mt-1 text-sm">You've earned the "{mod.badge?.label}" badge. Head back to the Academy to start your next module.</p>
            <Button onClick={() => navigate("/academy")} className="mt-3 bg-gold text-charcoal hover:bg-gold-hover" data-testid="complete-back-btn">Back to the Academy</Button>
          </div>
        )}

        <ModuleDiscussion moduleId={moduleId} prompt={mod.discussion_prompt} />
      </div>
    </div>
  );
}
